import { t, currentLocale } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Voice Dictation controller — uses the browser Web Speech API to transcribe
// spoken words into a Trix rich-text field or a plain textarea.
//
// Usage (Trix field):
//   <div data-controller="voice-dictation"
//        data-voice-dictation-trix-id-value="<trix-element-id>">
//     <button data-action="voice-dictation#toggle"
//             data-voice-dictation-target="button">
//       🎤 Dictate
//     </button>
//     <span data-voice-dictation-target="status"></span>
//   </div>
//
// Usage (plain textarea):
//   <div data-controller="voice-dictation"
//        data-voice-dictation-textarea-id-value="<textarea-id>">
//     ...same buttons...
//   </div>

export default class extends Controller {
  static targets = ["button", "status"]
  static values = {
    trixId: String,
    textareaId: String,
    language: { type: String, default: "en-US" }
  }

  connect() {
    this.recognition = null
    this.isListening = false

    if (!this.#speechSupported()) {
      this.#showUnsupported()
    }
  }

  disconnect() {
    this.#stopRecognition()
  }

  toggle() {
    if (this.isListening) {
      this.#stopRecognition()
    } else {
      this.#startRecognition()
    }
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  #speechSupported() {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
  }

  #showUnsupported() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.title = t("javascript.controllers.voice_dictation_controller.unsupported_browser")
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = t('javascript.controllers.voice_dictation_controller.not_supported')
      this.statusTarget.className = "text-xs text-gray-400 ml-2"
    }
  }

  #startRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    this.recognition = new SpeechRecognition()
    // Default to the page locale unless an explicit language is configured.
    this.recognition.lang = this.hasLanguageValue ? this.languageValue : currentLocale()
    this.recognition.interimResults = true
    this.recognition.continuous = true

    this.recognition.onstart = () => {
      this.isListening = true
      this.#updateButton(true)
      this.#setStatus(t("javascript.controllers.voice_dictation_controller.listening"), "text-xs text-red-500 ml-2 animate-pulse")
    }

    this.recognition.onresult = (event) => {
      let finalTranscript = ""
      let interimTranscript = ""

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          finalTranscript += transcript
        } else {
          interimTranscript += transcript
        }
      }

      if (finalTranscript) {
        this.#insertText(finalTranscript)
      }

      if (interimTranscript) {
        this.#setStatus(`"${interimTranscript}"`, "text-xs text-gray-500 ml-2 italic")
      }
    }

    this.recognition.onerror = (event) => {
      const messages = {
        "no-speech": t("javascript.controllers.voice_dictation_controller.no_speech"),
        "audio-capture": t("javascript.controllers.voice_dictation_controller.microphone_not_found"),
        "not-allowed": t("javascript.controllers.voice_dictation_controller.microphone_denied"),
        "network": t("javascript.controllers.voice_dictation_controller.network_error")
      }
      const msg = messages[event.error] || t("javascript.controllers.voice_dictation_controller.error_generic", { error: event.error })
      this.#setStatus(msg, "text-xs text-red-500 ml-2")
      this.#stopRecognition()
    }

    this.recognition.onend = () => {
      if (this.isListening) {
        // Auto-restarted if still toggled on (handles browser timeout)
        try {
          this.recognition.start()
        } catch (err) {
          console.warn("[voice-dictation] Could not restart recognition after timeout:", err)
          this.#resetState()
        }
      } else {
        this.#resetState()
      }
    }

    try {
      this.recognition.start()
    } catch (err) {
      this.#setStatus(t("javascript.controllers.voice_dictation_controller.could_not_start_microphone"), "text-xs text-red-500 ml-2")
    }
  }

  #stopRecognition() {
    this.isListening = false
    if (this.recognition) {
      try { this.recognition.stop() } catch (err) {
        console.warn("[voice-dictation] Error stopping recognition:", err)
      }
      this.recognition = null
    }
    this.#resetState()
  }

  #resetState() {
    this.isListening = false
    this.#updateButton(false)
    this.#setStatus("", "")
  }

  #updateButton(active) {
    if (!this.hasButtonTarget) return
    if (active) {
      this.buttonTarget.classList.add("ring-2", "ring-red-400")
      this.buttonTarget.setAttribute("aria-pressed", "true")
      this.buttonTarget.title = t("javascript.controllers.voice_dictation_controller.stop_dictation")
    } else {
      this.buttonTarget.classList.remove("ring-2", "ring-red-400")
      this.buttonTarget.setAttribute("aria-pressed", "false")
      this.buttonTarget.title = t("javascript.controllers.voice_dictation_controller.start_dictation")
    }
  }

  #setStatus(text, className) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.className = className
  }

  // Insert transcribed text into the target field.
  // Supports Lexical editors, legacy Trix editors, and plain textareas.
  #insertText(text) {
    // Lexical / Trix rich-text target (data-voice-dictation-trix-id-value points to the editor wrapper)
    if (this.hasTrixIdValue && this.trixIdValue) {
      const element = document.getElementById(this.trixIdValue)
      if (element) {
        // Try Lexical editor first (new default)
        const lexicalCtrl = this.application.getControllerForElementAndIdentifier(element, "lexical-editor")
        if (lexicalCtrl) {
          lexicalCtrl.insertText(text)
          return
        }
        // Fall back to legacy Trix
        if (element.editor) {
          const editor = element.editor
          editor.recordUndoEntry("Voice Dictation")
          const position = editor.getSelectedRange()
          editor.setSelectedRange([position[1], position[1]])
          editor.insertString(text + " ")
          return
        }
      }
    }

    // Plain textarea target
    if (this.hasTextareaIdValue && this.textareaIdValue) {
      const textarea = document.getElementById(this.textareaIdValue)
      if (textarea) {
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const before = textarea.value.substring(0, start)
        const after = textarea.value.substring(end)
        textarea.value = before + text + " " + after
        textarea.selectionStart = textarea.selectionEnd = start + text.length + 1
        textarea.dispatchEvent(new Event("input", { bubbles: true }))
        return
      }
    }

    console.warn("[voice-dictation] No target field found. Set data-voice-dictation-trix-id-value or data-voice-dictation-textarea-id-value.")
  }
}
